import { backOff } from 'exponential-backoff'

import {
  BigNumber,
  ContractTransaction,
  ContractReceipt,
} from 'ethers'

import {
  Bowie,
  TokenStorage,
  TokenPanEVision,
} from '../typechain'

import {
  ETH_STORAGE_SLOT_BYTES,
  IMAGE_BLOCKS_PER_TRANSACTION,
} from './constants'

export interface IStorageImageJobMeta {
  fileSize: number,
  totalBlocks: number,
  totalTransactions: number,
  completedTransactions: number,
  blocksPerTransaction: number,
  error?: any,
}

export interface IStorageImageJobCreateFileTransaction {
  name: string,
  size: number,
  gas: number,
  trx: ContractTransaction,
  receipt: ContractReceipt,
}

export interface IStorageImageTransaction {
  index: number,
  gas: number,
  trx: ContractTransaction,
  receipt: ContractReceipt,
}

export interface IStorageFinalizeTransaction {
  trx?: ContractTransaction,
  receipt?: ContractReceipt,
  error?: any,
}

export const base64ToUrl = (data: string): string => {
  return `data:image/jpeg;base64,${data}`
}

export const stringToBigNumber = (data: string): BigNumber => {
  return BigNumber.from(bufferToHex(Buffer.from(data, 'utf8')))
}

export const bigNumberToString = (data: BigNumber): string => {
  return Buffer.from(hexToBuffer(data.toHexString())).toString('utf8')
}

export const bufferToHex = (data: Uint8Array): string => {
  return '0x' + Array.prototype.map.call(data, x => ('00' + x.toString(16)).slice(-2)).join('')
}

export const bufferToBinary = (data: Uint8Array): string => {
  let binaryString = ''
  for(var i=0; i<data.length; i++) {
    binaryString += data[i].toString(2)
  }
  return binaryString
}

export const bufferToBase64 = (data: Uint8Array): string => {
  return Buffer.from(data).toString('base64')
}

export const hexToNumbers = (hex: string): number[] => {
  const data = hex.replace(/^0x/, '')
  const numbers: number[] = []
  for(var i = 0; i < data.length; i += 2) {
    const byte = data[i] + data[i+1]
    numbers.push(parseInt(byte, 16))
  }
  return numbers
}

export const hexToBuffer = (hex: string): Uint8Array => {
  return Uint8Array.from(hexToNumbers(hex))
}

// given an array of 8 bit numbers (i.e. file bytes)
// pack them into an array of 256 bit big numbers (i.e. eth storage slots)
export const encodeFile = (data: Uint8Array): BigNumber[] => {
  // chunk the data into 32 byte groups
  // the first number is always the byte size of the file
  // this is so we can pad storage slots and leading zeros
  // don't cause us problems
  const storageSlots: BigNumber[] = []
  for(var i = 0; i < data.length; i += ETH_STORAGE_SLOT_BYTES) {
    const hexString = bufferToHex(data.slice(i, i+ETH_STORAGE_SLOT_BYTES))
    storageSlots.push(BigNumber.from(hexString))
  }
  return storageSlots
}

export const packFile = (data: BigNumber[], blocksPerTransaction = IMAGE_BLOCKS_PER_TRANSACTION): BigNumber[][] => {
  // the actual blocks we will write for each transaction
  // we use the transaction index to reference
  const transactionBatches: BigNumber[][] = []
  let currentTransactionBatch: BigNumber[] = []

  data.forEach(block => {
    currentTransactionBatch.push(block)
    if(currentTransactionBatch.length >= blocksPerTransaction) {
      transactionBatches.push(currentTransactionBatch)
      currentTransactionBatch = []
    }
  })

  if(currentTransactionBatch.length > 0) {
    transactionBatches.push(currentTransactionBatch) 
  }

  return transactionBatches
}

export const unpackFile = (data: BigNumber[][]): BigNumber[] => {
  return data.reduce((all, trx) => {
    return all.concat(trx)
  }, [])
}

// given an array of bignumbers, process each into a uint8array
// pad each hex string to the correct length to account for leading zeros
export const decodeFile = (data: BigNumber[], filesize: BigNumber): Uint8Array => {
  const lastSlotSize = (filesize.toNumber() - ((data.length - 1) * ETH_STORAGE_SLOT_BYTES))
  let numbers: number[] = data.reduce<number[]>((all, storageSlot, slotIndex) => {
    let hexString = storageSlot.toHexString().replace(/^0x/, '')
    const slotBytes = slotIndex == data.length - 1 ?
      lastSlotSize :
      ETH_STORAGE_SLOT_BYTES
    while(hexString.length < slotBytes * 2) {
      hexString = '00' + hexString
    }
    return all.concat(hexToNumbers(hexString))
  }, [])

  return Uint8Array.from(numbers)
}

export interface IImageLoaderEvents {
  onChunk?: {
    (index: number, data: BigNumber[]): void
  },
}

// because the whole image is too big to read in a single transaction
// we loop over the same batches as we wrote with (to ensure the gas limit is not exceeded)
// even though it's a read op - it's still subject to the gas limit even though gas is not charged
export const extractFile = async (storage: TokenStorage, tokenId: BigNumber, fileName: BigNumber, events?: IImageLoaderEvents): Promise<BigNumber[]> => {
  const data: BigNumber[][] = []
  const batchCountRes = await backOff(() => storage.getFileBatchLength(tokenId, fileName))
  const batchCount = (batchCountRes).toNumber()
  if(batchCount <= 0) return []
  for(let i=0; i<batchCount; i++) {
    const batch = await backOff(() => storage.getFileBatchData(tokenId, fileName, BigNumber.from(i)))
    if(events?.onChunk) events.onChunk(i, batch)
    data.push(batch)
  }
  return unpackFile(data)
}

export const getFileSize = async (storage: TokenStorage, tokenId: BigNumber, fileName: BigNumber): Promise<BigNumber> => {
  const filenames = await backOff(() => storage.getFileNames(tokenId))
  const fileIndex = filenames.findIndex(f => f.toHexString() == fileName.toHexString())
  if(fileIndex < 0) throw new Error(`filename not found: ${tokenId.toHexString()} -> ${fileName.toHexString()}`)
  const filesizes = await backOff(() => storage.getFileSizes(tokenId))
  return filesizes[fileIndex]
}

export const extractFileBytes = async (storage: TokenStorage, tokenId: BigNumber, fileName: BigNumber, events?: IImageLoaderEvents): Promise<Uint8Array> => {
  const fileSize = await getFileSize(storage, tokenId, fileName)
  const storageNumbers = await extractFile(storage, tokenId, fileName, events)
  return decodeFile(storageNumbers, fileSize)
}

export const extractFilePanEVision = async (token: TokenPanEVision, tokenId: BigNumber, fileName: BigNumber, events?: IImageLoaderEvents): Promise<BigNumber[]> => {
  const data: BigNumber[][] = []
  const batchCount = (await token.fileBatchLength(tokenId, fileName)).toNumber()
  if(batchCount <= 0) return []
  for(let i=0; i<batchCount; i++) {
    const batch = await token.fileBatchData(tokenId, fileName, BigNumber.from(i))
    if(events?.onChunk) events.onChunk(i, batch)
    data.push(batch)
  }
  return unpackFile(data)
}

export const extractImagePanEVision = async (token: TokenPanEVision, tokenId: BigNumber, events?: IImageLoaderEvents): Promise<BigNumber[]> => {
  const data: BigNumber[][] = []
  const batchCount = (await token.imageBatchLength(tokenId)).toNumber()
  if(batchCount <= 0) return []
  for(let i=0; i<batchCount; i++) {
    const batch = await token.imageBatchData(tokenId, BigNumber.from(i))
    if(events?.onChunk) events.onChunk(i, batch)
    data.push(batch)
  }
  return unpackFile(data)
}

export const extractMetadataPanEVision = async (token: TokenPanEVision, tokenId: BigNumber, events?: IImageLoaderEvents): Promise<BigNumber[]> => {
  const data: BigNumber[][] = []
  const batchCount = (await token.metadataBatchLength(tokenId)).toNumber()
  if(batchCount <= 0) return []
  for(let i=0; i<batchCount; i++) {
    const batch = await token.metadataBatchData(tokenId, BigNumber.from(i))
    if(events?.onChunk) events.onChunk(i, batch)
    data.push(batch)
  }
  return unpackFile(data)
}

export const extractImageBowie = async (token: Bowie, events?: IImageLoaderEvents): Promise<BigNumber[]> => {
  const data: BigNumber[][] = []
  const batchCount = (await token.imageBatches()).toNumber()
  if(batchCount <= 0) return []
  for(let i=0; i<batchCount; i++) {
    const batch = await token.imageData(BigNumber.from(i))
    if(events?.onChunk) events.onChunk(i, batch)
    data.push(batch)
  }
  return unpackFile(data)
}

export const getFileSizePanEVision = async (token: TokenPanEVision, tokenId: BigNumber, fileName: BigNumber): Promise<BigNumber> => {
  const filenames = await token.fileNames(tokenId)
  const fileIndex = filenames.findIndex(f => f.toHexString() == fileName.toHexString())
  if(fileIndex < 0) throw new Error(`filename not found: ${tokenId.toHexString()} -> ${fileName.toHexString()}`)
  const filesizes = await token.fileSizes(tokenId)
  return filesizes[fileIndex]
}

export const extractFileBytesPanEVision = async (token: TokenPanEVision, tokenId: BigNumber, fileName: BigNumber, events?: IImageLoaderEvents): Promise<Uint8Array> => {
  const fileSize = await getFileSizePanEVision(token, tokenId, fileName)
  const storageNumbers = await extractFilePanEVision(token, tokenId, fileName, events)
  return decodeFile(storageNumbers, fileSize)
}

export const extractImageBytesPanEVision = async (token: TokenPanEVision, tokenId: BigNumber, events?: IImageLoaderEvents): Promise<Uint8Array> => {
  const fileSize = await token.imageSize(tokenId)
  const storageNumbers = await extractImagePanEVision(token, tokenId, events)
  return decodeFile(storageNumbers, fileSize)
}

export const extractImageBytesBowie = async (token: Bowie, events?: IImageLoaderEvents): Promise<Uint8Array> => {
  const fileSize = await token.imageSize()
  const storageNumbers = await extractImageBowie(token, events)
  return decodeFile(storageNumbers, fileSize)
}

export const extractMetadataBytesPanEVision = async (token: TokenPanEVision, tokenId: BigNumber, events?: IImageLoaderEvents): Promise<Uint8Array> => {
  const fileSize = await token.metadataSize(tokenId)
  const storageNumbers = await extractMetadataPanEVision(token, tokenId, events)
  return decodeFile(storageNumbers, fileSize)
}